import { RootStore } from "./stores/rootStore";
import { NavigateFunction } from "react-router-dom";
import { OrderType, OrderStatus } from "./interfaces_and_types";
import { runInAction } from "mobx";

/**
 * Unified notification payload protocol:
 * {
 *   type: string;          // event type: "new_order", "navigation", "order_cancelled", etc.
 *   data: {                // type-specific payload
 *     order?: OrderType;   // for order events
 *     page?: string;       // for navigation events
 *     popup?: boolean;     // for navigation events
 *     message?: string;    // for navigation events
 *     statusMessage?: boolean;
 *   };
 * }
 */
type NotificationPayload = {
  type: string;
  data: {
    order?: Partial<OrderType> | string | null;
    page?: string | null;
    popup?: boolean;
    message?: string;
    statusMessage?: boolean;
    [key: string]: any;
  };
};

interface Message {
  data: NotificationPayload;
}

/**
 * Universal message handler for push notifications via React Native WebView bridge
 * Handles order updates and navigation
 * @param e - MessageEvent from WebView bridge
 * @param store - MobX root store
 * @param navigate - React Router navigate function
 */
const onmessage = (
  e: MessageEvent<Message>,
  store: RootStore,
  navigate: NavigateFunction,
) => {
  const {
    orders,
    oldOrders,
    waitingReadyOrders,
    update_order_from_notification,
    fetchOrders,
    fetchOrdersStatus,
  } = store.orderStore;
  const { token } = store.placeStore;
  const { currentPage, setCurrentPage } = store.drawerStore;
  const {
    setNotificationPopupOpen,
    setNotificationPopupMessage,
    setStatusMessage,
    setNewOrderAlertVisible,
  } = store.placeStore;

  const message: Message =
    typeof e.data === "string" ? JSON.parse(e.data) : e.data;

  const parsed: NotificationPayload =
    typeof message.data === "string" ? JSON.parse(message.data) : message.data;

  // --- Navigation (type="navigation") ---
  if (parsed.type === "navigation") {
    const { page, popup, message: navMessage, statusMessage } = parsed.data;
    if (page && currentPage !== page) {
      navigate(page === "budgetPage" ? "/place/budget" : "/");
      runInAction(() => setCurrentPage(page ?? ""));
    }
    if (popup) {
      runInAction(() => {
        setNotificationPopupOpen(true);
        setNotificationPopupMessage(navMessage ?? "");
      });
    }
    if (statusMessage && navMessage) {
      runInAction(() => setStatusMessage(navMessage));
    }
  }

  // --- Order data ---
  if (
    orders !== undefined &&
    oldOrders !== undefined &&
    parsed.data?.order != null
  ) {
    const parsedOrder: Partial<OrderType> =
      typeof parsed.data.order === "string"
        ? JSON.parse(parsed.data.order)
        : parsed.data.order;

    if (parsedOrder.id != null) {
      // Show full-screen alert only for genuinely new waiting orders (not already in store)
      const isNewOrder =
        parsedOrder.status != null &&
        parsedOrder.status <= OrderStatus.SENT_TO_RESTAURANT &&
        !orders.find((o) => o.id === parsedOrder.id);
      if (isNewOrder) {
        runInAction(() => setNewOrderAlertVisible(true));
      }

      const knownOrder =
        orders.find((o) => o.id === parsedOrder.id) ??
        waitingReadyOrders?.find((o) => o.id === parsedOrder.id) ??
        oldOrders.find((o) => o.id === parsedOrder.id);
      const hasFullData = Array.isArray(parsedOrder.items);

      if (knownOrder || hasFullData) {
        runInAction(() => update_order_from_notification(parsedOrder));
      } else if (token !== null) {
        // Push payloads carry only a thin snapshot (id/status/flags — no items,
        // no price). Rendering it would crash the order card, so for orders we
        // don't have yet, pull the full list from the API instead.
        fetchOrders(token).catch((err) =>
          console.error("Order refetch after push failed:", err),
        );
        fetchOrdersStatus(token).catch((err) =>
          console.error("Order status refetch after push failed:", err),
        );
      } else {
        // Queued pushes are flushed on WEBVIEW_READY, which can beat the auth
        // token loading from storage — flag it so App refetches once auth is up.
        store.orderStore.setPendingPushRefetch(true);
      }
    }
  }
};

export default onmessage;
